// eventsData.ts, types, type config, sample fallback events, helpers for
// CareerEventsV2. Static fallback data carried over verbatim from v1 (shown only
// when neither a live search nor cached DB events are available).

import {
  Users, Building2, Handshake, Monitor, Presentation, GraduationCap,
  type LucideIcon,
} from "lucide-react";

// i18n 2026-07-25: this screen's copy lives in module-level config objects,
// which cannot call a hook. Rather than fork every interface, English is kept
// in a lookup keyed by the German source string and resolved at the render
// site with tl(loc, x, EN_COPY[x] ?? x) — unmapped values fall through.
export const EN_COPY: Record<string, string> = {
  "Deutschlands größte Jobmesse für Studenten und Absolventen, über 400 Aussteller aus allen Branchen, kostenloser CV-Check und Bewerbungsfotos.": "Germany's largest careers fair for students and graduates — over 400 exhibitors across every industry, plus a free CV check and application photos.",
  "Studien- und Ausbildungsmesse für Schüler und Abiturienten. Universitäten, Hochschulen und Ausbildungsbetriebe stellen sich vor.": "A study and training fair for school leavers. Universities, colleges and training employers introduce themselves.",
  "Networking-Event für Frauen in der Tech-Branche mit Keynotes, Panel-Diskussionen und Speed-Mentoring.": "A networking event for women in tech, with keynotes, panel discussions and speed mentoring.",
  "Fach- und Führungskräftemesse mit über 100 Top-Arbeitgebern. Schwerpunkt: Finance, Consulting, IT und Ingenieurwesen.": "A fair for professionals and managers with over 100 leading employers. Focus: finance, consulting, IT and engineering.",
  "Kostenfreies Webinar zu Remote-Karrieremöglichkeiten und digitalen Berufsbildern.": "A free webinar on remote career options and digital job profiles.",
  "Studentisch organisierte Firmenkontaktmesse mit Fokus auf MINT-Berufe und direktem Austausch mit Ingenieurbüros.": "A student-run company fair focused on STEM careers, with direct contact to engineering firms.",
  "Infoveranstaltung zu berufsbegleitenden Master-Studiengängen. In 5 Minuten zum nächsten Karriereschritt.": "An information session on part-time Master's programmes. Your next career step in 5 minutes.",
  "vocatium-Konzept: terminierte Gespräche mit Ausstellern, Bildungseinrichtungen und Arbeitgebern in der Region Oberbayern.": "The vocatium format: scheduled conversations with exhibitors, education providers and employers across the Oberbayern region.",
};


export interface EventLocation { venue: string; city: string; address: string }

export interface CareerEvent {
  id: string; name: string; type: string; date: string; time: string;
  location: EventLocation; organizer: string; company: string | null;
  description: string; why_relevant: string; target_audience: string[];
  industries: string[]; is_free: boolean; registration_required: boolean;
  format: string; highlights: string[]; companies_attending: string[];
  image_url?: string; url?: string;
}

export interface Tip { title: string; titleEn?: string; text: string; textEn?: string }
export interface SearchResult { events: CareerEvent[]; total_found: number; search_summary: string; tips: Tip[] }

/** type → label + icon + a token tone (drives the soft tint via data-type in CSS). */
export const TYPE_CONFIG: Record<string, { label: string; labelEn: string; icon: LucideIcon; tone: string }> = {
  karrieremesse:  { label: "Karrieremesse",  labelEn: "Career fair",     icon: Users,         tone: "primary" },
  firmenevent:    { label: "Firmenevent",    labelEn: "Company event",   icon: Building2,     tone: "ink" },
  networking:     { label: "Networking",     labelEn: "Networking",      icon: Handshake,     tone: "success" },
  webinar:        { label: "Webinar",        labelEn: "Webinar",         icon: Monitor,       tone: "warn" },
  workshop:       { label: "Workshop",       labelEn: "Workshop",        icon: Presentation,  tone: "destructive" },
  hochschulmesse: { label: "Hochschulmesse", labelEn: "University fair", icon: GraduationCap, tone: "accent" },
};

export function formatDateShort(dateStr: string): { day: string; month: string } {
  const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const parts = dateStr.split(".");
  if (parts.length < 3) return { day: dateStr, month: "" };
  return { day: parts[0], month: months[parseInt(parts[1], 10) - 1] || parts[1] };
}

/** True when the event date (DD.MM.YYYY) is today or later. Unknown formats
 *  return true (don't hide) so a real feed with a different format still shows;
 *  this only stops stale hard-coded fallbacks from reading as upcoming (P3.4). */
export function eventIsUpcoming(dateStr: string): boolean {
  const parts = (dateStr || "").split(".");
  if (parts.length < 3) return true;
  const d = parseInt(parts[0], 10), m = parseInt(parts[1], 10), y = parseInt(parts[2], 10);
  if (!d || !m || !y) return true;
  const ev = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return ev.getTime() >= today.getTime();
}

// General advice for any career event (shown next to real events only; the invented
// sample events that used to sit here were shown as real and are gone).
export const EVENT_TIPS: SearchResult["tips"] = [
    { title: "Lebenslauf mitbringen", titleEn: "Bring your CV", text: "Drucke 10–15 Kopien deines aktuellen CVs aus, viele Recruiter wollen ihn direkt mitnehmen.", textEn: "Print 10–15 copies of your CV, many recruiters want to take one on the spot." },
    { title: "Elevator Pitch vorbereiten", titleEn: "Prepare an elevator pitch", text: "Übe eine 30-Sekunden-Vorstellung: Wer bist du, was kannst du, was suchst du?", textEn: "Practise a 30-second intro: who you are, what you can do, what you're looking for." },
    { title: "Firmen vorab recherchieren", titleEn: "Research companies in advance", text: "Wähle 5–7 Wunscharbeitgeber und bereite gezielte Fragen vor.", textEn: "Pick 5–7 target employers and prepare specific questions for each." },
    { title: "Visitenkarten / QR-Code", titleEn: "Business card / QR code", text: "Erstelle eine digitale Kontaktkarte mit QR-Code, das wirkt professionell und modern.", textEn: "Create a digital contact card with a QR code, it looks professional and modern." },
  ];
